import { ref, computed, watch, onMounted } from 'vue'
import { Keypair } from '@solana/web3.js'
import bs58 from 'bs58'
import { i18n } from '@/i18n'
import { useDexTrade } from '@/composable/useDexTrade'
import type { EvmSwapResult, SolSwapResult } from '@/composable/useDexTrade'
import { useMyToast } from '@/composable/useMyToast'
import { STABLE_ADDRESSES, SOL_USDT_ADDRESS, CHAIN_RPCS } from '@/utils/consts'
import { toBigInt, calcDecimals } from '@/utils'
import { use1InchStore } from '@/stores/oneInchStore'
import { useJupStore } from '@/stores/jupiterStore'
import { useLocalKeys } from '@/composable/useLocalKeys'
import { useWeb3 } from '@/composable/useWeb3'
import { useUserStore } from '@/stores/userStore'
import type { TokenInfo } from '@/services/jupiter-service/types'
import type { OrderSide, CcxtMarket } from '@/services/types'
import type { Ref } from 'vue'

export interface DexSwapResult {
  success: boolean
  txHash?: string
  effectiveAmount?: number
  error?: string
}

export interface UseDexSwapFormOptions {
  chain: Ref<string>
  contract: Ref<string>
  asset: Ref<string>
  amount: Ref<number | null>
  dexPriceBuy: Ref<number | null>
  dexPriceSell: Ref<number | null>
  slippage: Ref<number | null>
  market: Ref<CcxtMarket>
}

export function useDexSwapForm(options: UseDexSwapFormOptions) {
  const { t } = i18n.global
  const { addToast } = useMyToast()
  const dexTrade = useDexTrade()
  const oneInchStore = use1InchStore()
  const jupStore = useJupStore()
  const localKeys = useLocalKeys()
  const userStore = useUserStore()

  const swapping = ref(false)
  const usdtBalance = ref<number | null>(null)
  const tokenBalance = ref<number | null>(null)
  const balancesLoading = ref(false)

  const chainLower = computed(() => options.chain.value.toLowerCase())
  const priceRounding = computed(() => calcDecimals(options.market.value.precision.price))

  // --- Decimals cache ---
  const DECIMALS_CACHE_KEY = 'sr-decimals-cache'
  const decimalsCache = ref<Record<string, number>>({})

  const loadDecimalsCache = () => {
    try {
      const raw = localStorage.getItem(DECIMALS_CACHE_KEY)
      if (raw) {
        decimalsCache.value = JSON.parse(raw)
      }
    } catch {
      // ignore corrupt cache
    }
  }

  const saveDecimalsCache = () => {
    try {
      localStorage.setItem(DECIMALS_CACHE_KEY, JSON.stringify(decimalsCache.value))
    } catch {
      // ignore storage errors
    }
  }

  const getDecimalsCached = async (tokenAddress: string): Promise<number> => {
    const cacheKey = `${chainLower.value}/${tokenAddress.toLowerCase()}`
    if (decimalsCache.value[cacheKey] !== undefined) {
      return decimalsCache.value[cacheKey]
    }
    const { initWeb3, getTokenDecimals } = useWeb3()
    const rpcs = CHAIN_RPCS[chainLower.value]
    if (!rpcs?.length) {
      throw new Error('Unsupported chain')
    }
    initWeb3(rpcs)
    const decimals = await getTokenDecimals(tokenAddress)
    decimalsCache.value[cacheKey] = decimals
    saveDecimalsCache()
    return decimals
  }

  // --- Signal ---
  const EXPLORERS: Record<string, string> = {
    eth: 'https://etherscan.io/tx/',
    bsc: 'https://bscscan.com/tx/',
    base: 'https://basescan.org/tx/',
    sol: 'https://solscan.io/tx/',
  }

  const sendDexSignal = (
    side: OrderSide,
    price: number,
    amount: number,
    chain: string,
    txHash: string,
  ) => {
    const currentUrl = new URL(window.location.href)
    currentUrl.searchParams.delete('trigger')
    currentUrl.searchParams.delete('auto')
    currentUrl.searchParams.delete('autoSl')
    currentUrl.searchParams.delete('autoTp')
    currentUrl.searchParams.delete('avg')
    currentUrl.searchParams.delete('debug')
    currentUrl.searchParams.delete('nocharts')
    const cleanUrl = currentUrl.toString()

    const assetName = options.asset.value.toUpperCase()
    const sideLabel = t(side)
    const explorerUrl = EXPLORERS[chainLower.value]
    const txLink = explorerUrl && txHash ? `<a href="${explorerUrl}${txHash}">[tx]</a>` : txHash
    const orderCost = amount * price

    let content = `<b>DEX Swap</b>\n\n`
    content += `<a href="${cleanUrl}"><b>✅ Успех! ${assetName} ${chain.toUpperCase()}</b></a>\n\n`
    content += `Сторона: ${sideLabel}\n`
    content += `Цена: ${price.toFixed(priceRounding.value)}\n`
    content += `Объём: ${amount} ${assetName}\n`
    content += `${t('modal.createSwapOrders.orderCost')}: ${orderCost.toFixed(2)} USDT\n`
    content += `Tx: ${txLink}`

    userStore.sendSignal(content)
  }

  // --- Balances ---
  const fetchBalances = async () => {
    balancesLoading.value = true
    try {
      if (chainLower.value === 'sol') {
        await fetchBalancesSol()
      } else {
        await fetchBalancesEvm()
      }
    } finally {
      balancesLoading.value = false
    }
  }

  const fetchBalancesSol = async () => {
    const apiKey = localKeys.get('jup-api')
    const privateKey = localKeys.get('jup-xprv')
    if (!apiKey || !privateKey) {
      usdtBalance.value = null
      tokenBalance.value = null
      return
    }

    try {
      const balances = await dexTrade.getSolBalance()
      if (!balances || typeof balances !== 'object' || 'error' in balances) {
        usdtBalance.value = null
        tokenBalance.value = null
        return
      }

      const b = balances as Record<string, { uiAmount: number }>
      const usdtEntry = Object.entries(b).find(
        ([k]) => k.toLowerCase() === SOL_USDT_ADDRESS.toLowerCase(),
      )
      usdtBalance.value = usdtEntry ? usdtEntry[1].uiAmount : 0

      const tokenEntry = Object.entries(b).find(
        ([k]) => k.toLowerCase() === options.contract.value.toLowerCase(),
      )
      tokenBalance.value = tokenEntry ? tokenEntry[1].uiAmount : 0
    } catch {
      usdtBalance.value = null
      tokenBalance.value = null
    }
  }

  const fetchBalancesEvm = async () => {
    const privateKey = localKeys.get('oneinch-key-evm')
    if (!privateKey) {
      usdtBalance.value = null
      tokenBalance.value = null
      return
    }

    const quoteAddress = STABLE_ADDRESSES[chainLower.value]
    if (!quoteAddress) {
      usdtBalance.value = null
      tokenBalance.value = null
      return
    }

    try {
      const { initWeb3, getPublicKey, getTokenBalance } = useWeb3()
      const rpcs = CHAIN_RPCS[chainLower.value]
      if (!rpcs?.length) {
        usdtBalance.value = null
        tokenBalance.value = null
        return
      }
      initWeb3(rpcs)

      const publicKey = getPublicKey(privateKey)
      const [usdtRaw, tokenRaw, usdtDecimals, tokenDecimals] = await Promise.all([
        getTokenBalance(quoteAddress, publicKey),
        getTokenBalance(options.contract.value, publicKey),
        getDecimalsCached(quoteAddress),
        getDecimalsCached(options.contract.value),
      ])

      usdtBalance.value = Number(usdtRaw) / Math.pow(10, usdtDecimals)
      tokenBalance.value = Number(tokenRaw) / Math.pow(10, tokenDecimals)
    } catch {
      usdtBalance.value = null
      tokenBalance.value = null
    }
  }

  // --- Quote checking ---
  const checkQuotePriceSol = async (side: OrderSide, amount: number): Promise<number> => {
    const apiKey = localKeys.get('jup-api')
    const privateKey = localKeys.get('jup-xprv')
    if (!apiKey || !privateKey) {
      throw new Error('Jupiter keys not found')
    }
    jupStore.apiKey = apiKey

    const wallet = Keypair.fromSecretKey(bs58.decode(privateKey))
    const publicKey = wallet.publicKey.toBase58()

    let tokenBaseData: TokenInfo | undefined
    try {
      const searchRes = await jupStore.search(options.contract.value)
      if (Array.isArray(searchRes) && searchRes.length > 0) {
        tokenBaseData = searchRes[0]
      }
    } catch {
      throw new Error('Failed to fetch token data')
    }

    if (!tokenBaseData) {
      throw new Error('Token not found')
    }

    const baseDecimals = tokenBaseData.decimals
    const quoteDecimals = 6

    if (side === 'buy') {
      if (!options.dexPriceBuy.value) throw new Error('DEX buy price not available')
      const decAmount = Math.round(options.dexPriceBuy.value * amount * Math.pow(10, quoteDecimals))
      const orderBuy = await jupStore.getOrder(SOL_USDT_ADDRESS, options.contract.value, decAmount, publicKey)
      if (!orderBuy || 'error' in orderBuy) {
        const errMsg = (orderBuy as unknown as Record<string, string>)?.error || 'insufficient liquidity'
        throw new Error(errMsg)
      }
      const outAmount = Number((orderBuy as unknown as Record<string, unknown>)?.outAmount ?? 0)
      const tokensReceived = outAmount / Math.pow(10, baseDecimals)
      return tokensReceived > 0 ? (decAmount / Math.pow(10, quoteDecimals)) / tokensReceived : 0
    } else {
      if (!options.dexPriceSell.value) throw new Error('DEX sell price not available')
      const decAmount = Math.round(amount * Math.pow(10, baseDecimals))
      const orderSell = await jupStore.getOrder(options.contract.value, SOL_USDT_ADDRESS, decAmount, publicKey)
      if (!orderSell || 'error' in orderSell) {
        const errMsg = (orderSell as unknown as Record<string, string>)?.error || 'insufficient liquidity'
        throw new Error(errMsg)
      }
      const outAmount = Number((orderSell as unknown as Record<string, unknown>)?.outAmount ?? 0)
      return outAmount / Math.pow(10, quoteDecimals) / amount
    }
  }

  const checkQuotePriceEvm = async (
    side: OrderSide,
    baseDecimals: number,
    quoteDecimals: number,
    amount: number,
  ): Promise<number> => {
    const apiKey = localKeys.get('oneinch-api')
    if (!apiKey) {
      throw new Error('1inch API key not found')
    }
    oneInchStore.apiKey = apiKey

    const quoteAddress = STABLE_ADDRESSES[chainLower.value]
    if (!quoteAddress) {
      throw new Error('Unsupported chain')
    }

    if (side === 'buy') {
      if (!options.dexPriceBuy.value) throw new Error('DEX buy price not available')
      const decAmount = toBigInt(options.dexPriceBuy.value * amount * Math.pow(10, quoteDecimals))
      const quoteBuy_ = await oneInchStore.fetchQuote(
        chainLower.value,
        quoteAddress,
        options.contract.value,
        decAmount,
      )
      if (quoteBuy_ && 'error' in quoteBuy_) {
        const err = quoteBuy_ as unknown as Record<string, string>
        throw new Error(err.description || err.error || 'insufficient liquidity')
      }
      const quoteBuy = quoteBuy_ as { dstAmount: string }
      const tokensReceived = Number(quoteBuy.dstAmount) / Math.pow(10, baseDecimals)
      return tokensReceived > 0 ? (Number(decAmount) / Math.pow(10, quoteDecimals)) / tokensReceived : 0
    } else {
      if (!options.dexPriceSell.value) throw new Error('DEX sell price not available')
      const decAmount = toBigInt(amount * Math.pow(10, baseDecimals))
      const quoteSell_ = await oneInchStore.fetchQuote(
        chainLower.value,
        options.contract.value,
        quoteAddress,
        decAmount,
      )
      if (quoteSell_ && 'error' in quoteSell_) {
        const err = quoteSell_ as unknown as Record<string, string>
        throw new Error(err.description || err.error || 'insufficient liquidity')
      }
      const quoteSell = quoteSell_ as { dstAmount: string }
      return Number(quoteSell.dstAmount) / Math.pow(10, quoteDecimals) / amount
    }
  }

  // --- Swap execution ---
  const swapDexSol = async (side: OrderSide, amount: number): Promise<SolSwapResult> => {
    const apiKey = localKeys.get('jup-api')
    const privateKey = localKeys.get('jup-xprv')
    if (!apiKey || !privateKey) {
      throw new Error('Jupiter keys not found')
    }

    let tokenBaseData: TokenInfo | undefined
    try {
      const searchRes = await jupStore.search(options.contract.value)
      if (Array.isArray(searchRes) && searchRes.length > 0) {
        tokenBaseData = searchRes[0]
      }
    } catch {
      throw new Error('Failed to fetch token data')
    }

    if (!tokenBaseData) {
      throw new Error('Token not found')
    }

    const baseDecimals = tokenBaseData.decimals
    const quoteDecimals = 6

    if (side === 'buy') {
      if (!options.dexPriceBuy.value) throw new Error('DEX buy price not available')
      const decAmount = Math.round(options.dexPriceBuy.value * amount * Math.pow(10, quoteDecimals))
      return await dexTrade.buySol(SOL_USDT_ADDRESS, options.contract.value, decAmount)
    } else {
      const decAmount = Math.round(amount * Math.pow(10, baseDecimals))
      return await dexTrade.sellSol(options.contract.value, SOL_USDT_ADDRESS, decAmount)
    }
  }

  const swapDexEvm = async (side: OrderSide, amount: number): Promise<EvmSwapResult> => {
    const quoteAddress = STABLE_ADDRESSES[chainLower.value]
    if (!quoteAddress) {
      throw new Error('Unsupported chain')
    }

    const [baseDecimals, quoteDecimals] = await Promise.all([
      getDecimalsCached(options.contract.value),
      getDecimalsCached(quoteAddress),
    ])

    const slippage = options.slippage.value ?? 1

    if (side === 'buy') {
      if (!options.dexPriceBuy.value) throw new Error('DEX buy price not available')
      const decAmount = toBigInt(options.dexPriceBuy.value * amount * Math.pow(10, quoteDecimals))
      return await dexTrade.buyEvm(chainLower.value, quoteAddress, options.contract.value, decAmount, slippage)
    } else {
      const decAmount = toBigInt(amount * Math.pow(10, baseDecimals))
      return await dexTrade.sellEvm(chainLower.value, options.contract.value, quoteAddress, decAmount, slippage)
    }
  }

  const swapDex = async (side: OrderSide): Promise<DexSwapResult> => {
    const rawAmount = options.amount.value
    if (!rawAmount) {
      return { success: false }
    }

    const effectiveAmount =
      side === 'sell' && tokenBalance.value !== null
        ? Math.min(rawAmount, tokenBalance.value)
        : rawAmount

    if (effectiveAmount <= 0) {
      addToast({
        message: t('modal.createSwapOrders.errors.insufficientFunds'),
        severity: 'error',
      })
      return { success: false }
    }

    swapping.value = true
    try {
      let quotePrice: number

      if (chainLower.value === 'sol') {
        quotePrice = await checkQuotePriceSol(side, effectiveAmount)
      } else {
        const quoteAddress = STABLE_ADDRESSES[chainLower.value]
        if (!quoteAddress) throw new Error('Unsupported chain')

        const [baseDecimals, quoteDecimals] = await Promise.all([
          getDecimalsCached(options.contract.value),
          getDecimalsCached(quoteAddress),
        ])

        quotePrice = await checkQuotePriceEvm(side, baseDecimals, quoteDecimals, effectiveAmount)
      }

      const expectedPrice = side === 'buy' ? options.dexPriceBuy.value : options.dexPriceSell.value
      if (!expectedPrice) {
        throw new Error('Expected price not available')
      }

      const slippagePercent = (Math.abs(quotePrice - expectedPrice) / expectedPrice) * 100
      if (slippagePercent > (options.slippage.value ?? 0.25)) {
        throw new Error(
          `Price slipped: expected ${expectedPrice.toFixed(priceRounding.value)}, got ${quotePrice.toFixed(priceRounding.value)} (${slippagePercent.toFixed(2)}%)`,
        )
      }

      let result: EvmSwapResult | SolSwapResult
      if (chainLower.value === 'sol') {
        result = await swapDexSol(side, effectiveAmount)
      } else {
        result = await swapDexEvm(side, effectiveAmount)
      }

      const orderCost = effectiveAmount * expectedPrice
      addToast({
        message:
          t('modal.createSwapOrders.dexSwapSuccess', {
            amount: effectiveAmount,
            asset: side === 'buy' ? options.asset.value.toUpperCase() : 'USDT',
          }) +
          ` (${t('modal.createSwapOrders.orderCost')}: ${orderCost.toFixed(2)} USDT)`,
        severity: 'success',
      })

      fetchBalances()

      const txHash = (result as EvmSwapResult).hash ?? (result as SolSwapResult).signature ?? ''
      sendDexSignal(
        side,
        expectedPrice,
        effectiveAmount,
        options.chain.value,
        txHash,
      )

      return { success: true, txHash, effectiveAmount }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      const lowered = msg.toLowerCase()
      const isFundsError =
        lowered.includes('insufficient funds') ||
        lowered.includes('insufficient balance') ||
        lowered.includes('not enough') ||
        lowered.includes('erc20: transfer amount exceeds balance')
      addToast({
        message: isFundsError ? t('modal.createSwapOrders.errors.insufficientFunds') : msg,
        severity: 'error',
      })
      return { success: false, error: msg }
    } finally {
      swapping.value = false
    }
  }

  // --- Watchers ---
  watch(
    () => [options.chain.value, options.contract.value, options.amount.value],
    () => {
      fetchBalances()
    },
    { immediate: true },
  )

  onMounted(() => {
    loadDecimalsCache()
    fetchBalances()
  })

  return {
    swapping,
    usdtBalance,
    tokenBalance,
    balancesLoading,
    fetchBalances,
    swapDex,
  }
}
